import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import { userKeys } from "./user.keys";
import { UserDto, WorkPlanDto, WeeklyWorkDayDto, VersionWorkPlanDto } from "../../api-client";
import { calendarKeys } from "../Calendar/calendar.keys";
import {
  WorkingHoursCacheSnapshot,
  optimisticBreakPatch,
  restoreWorkingHoursCaches,
} from "../Calendar/AppointmentsOfAllTypes.utils";

/**
 * NOTE: Global error/success handling is configured in src/stores/queryClient.ts
 * - MutationCache handles automatic error notifications via notificationStore
 * - MutationCache handles automatic success notifications via meta.successMessage
 * - No need to manually call notificationStore in individual mutations
 */

/**
 * Mutation to save/update user information
 * Replaces UserStore.saveUserInfo
 */
export const useSaveUserInfo = () => {
  const { user: userApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (user: UserDto): Promise<UserDto> => {
      // The proxied client already unwraps axios `data`, so `response` is the
      // raw API payload: { users: UserDto, saved: UserDto } (single objects).
      const response = await userApi.saveUserInfo(user);
      return ((response as any)?.saved as UserDto) || user;
    },
    onSuccess: async (data, variables) => {
      const userId = data?.id || variables.id;

      if (userId) {
        // Seed the detail cache with the freshly saved user (includes the
        // merged role + user permission list from the backend).
        queryClient.setQueryData(userKeys.userById.detail(userId), data);
        await queryClient.invalidateQueries({
          queryKey: userKeys.userByIdWithTherapies.detail(userId),
        });
        // Invalidate the user by id query (used by UserLayout)
        await queryClient.invalidateQueries({
          queryKey: userKeys.userById.detail(userId),
        });
      }

      // Invalidate all users basic list to reflect changes
      await queryClient.invalidateQueries({
        queryKey: userKeys.allUsersBasic.all(),
      });
      // Admin list is cached per location; every location's copy may show this user.
      await queryClient.invalidateQueries({
        queryKey: userKeys.usersByLocation.all(),
      });
    },
    meta: {
      successMessage: "Benutzerdaten wurden erfolgreich gespeichert!",
      errorMessage: "Fehler beim Speichern der Benutzerdaten",
    },
  });
};

/**
 * Mutation to create a work plan
 * Replaces UserStore.createWorkPlan
 */
export const useCreateWorkPlan = () => {
  const { user: userApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workPlan: WorkPlanDto) => userApi.createWorkPlan(workPlan),
    onSuccess: async (data, variables) => {
      const userId = variables.employeeId;
      if (userId) {
        await queryClient.invalidateQueries({
          queryKey: userKeys.workPlans.detail(userId),
        });
      }
    },
    meta: {
      successMessage: "Arbeitsplan erfolgreich hinzugefügt",
      errorMessage: "Fehler beim Hinzufügen des Arbeitsplans",
    },
  });
};

/**
 * Mutation to update a work plan
 * Replaces UserStore.putWorkPlan
 */
export const useUpdateWorkPlan = () => {
  const { user: userApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workPlan: WorkPlanDto) => userApi.userProfileControllerPutWorkPlan(workPlan),
    onSuccess: async (data, variables) => {
      const userId = variables.employeeId;
      if (userId) {
        await queryClient.invalidateQueries({
          queryKey: userKeys.workPlans.detail(userId),
        });
      }
    },
    meta: {
      successMessage: "Arbeitsplan aktualisiert",
      errorMessage: "Fehler beim Aktualisieren des Arbeitsplans",
    },
  });
};

/**
 * Mutation to version a work plan (create new version when plan has historical data).
 * This closes the existing plan at the effective date and creates a new plan
 * with the user's changes starting from that date.
 */
export const useVersionWorkPlan = () => {
  const { user: userApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: VersionWorkPlanDto & { employeeId: string }) => {
      const { employeeId, ...versionDto } = dto;
      return userApi.versionWorkPlan(versionDto);
    },
    onSuccess: async (data, variables) => {
      const { employeeId } = variables;
      if (employeeId) {
        // Invalidate work plans to reflect the closed old plan and new versioned plan
        await queryClient.invalidateQueries({
          queryKey: userKeys.workPlans.detail(employeeId),
        });
        // Invalidate weekly work days as they now belong to the new plan
        await queryClient.invalidateQueries({
          queryKey: userKeys.weeklyWorkDays.byUser(employeeId),
        });
      }
    },
    meta: {
      successMessage: "Neue Arbeitsplan-Version erstellt",
      errorMessage: "Fehler beim Erstellen der neuen Arbeitsplan-Version",
    },
  });
};

/**
 * Mutation to delete a work plan
 * Replaces UserStore.deleteWorkPlan
 */
export const useDeleteWorkPlan = () => {
  const { user: userApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workPlanId }: { workPlanId: number; userId: string }) =>
      userApi.deleteWorkPlan(workPlanId),
    onSuccess: async (data, variables) => {
      const { userId, workPlanId } = variables;
      if (userId) {
        await queryClient.invalidateQueries({
          queryKey: userKeys.workPlans.detail(userId),
        });
        // Also invalidate weekly work days as they depend on work plans.
        // BOTH keys: the Arbeitstag list reads `byWorkPlan`, so invalidating
        // only `byUser` left it showing the deleted plan's rows and its hours
        // total (MD-351).
        await queryClient.invalidateQueries({
          queryKey: userKeys.weeklyWorkDays.byUser(userId),
        });
        await queryClient.invalidateQueries({
          queryKey: userKeys.weeklyWorkDays.byWorkPlan(userId, workPlanId),
        });
      }
      // A deleted plan changes the derived working hours too — same
      // invalidations the weekly-work-day mutations already do, so the
      // calendar and the hours total do not keep the removed plan.
      queryClient.invalidateQueries({ queryKey: calendarKeys.workingHours.all() });
      queryClient.invalidateQueries({
        queryKey: calendarKeys.continuousWorkPlansForUsers.all(),
      });
    },
    onError: (_error, variables) => {
      // An aborted delete may already have removed rows, so the screen has to
      // refetch here too — same keys as the success path.
      const { userId, workPlanId } = variables;
      if (userId) {
        queryClient.invalidateQueries({ queryKey: userKeys.workPlans.detail(userId) });
        queryClient.invalidateQueries({ queryKey: userKeys.weeklyWorkDays.byUser(userId) });
        queryClient.invalidateQueries({
          queryKey: userKeys.weeklyWorkDays.byWorkPlan(userId, workPlanId),
        });
      }
      queryClient.invalidateQueries({ queryKey: calendarKeys.workingHours.all() });
      queryClient.invalidateQueries({
        queryKey: calendarKeys.continuousWorkPlansForUsers.all(),
      });
    },
    meta: {
      successMessage: "Arbeitsplan gelöscht",
      errorMessage: "Fehler beim Löschen des Arbeitsplans",
    },
  });
};

/**
 * Mutation to create a weekly work day
 * Replaces UserStore.createWeeklyWorkDay
 */
export const useCreateWeeklyWorkDay = () => {
  const { user: userApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (weeklyWorkDay: WeeklyWorkDayDto) => userApi.createWeeklyWorkDay(weeklyWorkDay),
    onSuccess: async (data, variables) => {
      const userId = variables.employeeId;
      const workPlanId = variables.workPlan?.id;

      if (userId && workPlanId) {
        await queryClient.invalidateQueries({
          queryKey: userKeys.weeklyWorkDays.byWorkPlan(userId, workPlanId),
        });
      }
      if (userId) {
        await queryClient.invalidateQueries({
          queryKey: userKeys.weeklyWorkDays.byUser(userId),
        });
      }
    },
    meta: {
      successMessage: "Arbeitszeit erfolgreich hinzugefügt",
      errorMessage: "Fehler beim Hinzufügen der Arbeitszeit",
    },
  });
};

/**
 * Mutation to update a weekly work day
 * Replaces UserStore.updateWeeklyWorkDay
 */
export const useUpdateWeeklyWorkDay = () => {
  const { user: userApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (weeklyWorkDay: WeeklyWorkDayDto) => userApi.putWeeklyWorkDay(weeklyWorkDay),
    onMutate: async (weeklyWorkDay) => {
      // Provisional patch of the continuous lunch break's window across the
      // workingHours caches so the break does not snap back while the weekly work
      // day update is in flight. The continuous break is tied to a weekly work day.
      if (
        weeklyWorkDay.id == null ||
        !weeklyWorkDay.startTime ||
        !weeklyWorkDay.endTime ||
        !weeklyWorkDay.employeeId
      ) {
        return { snapshot: undefined };
      }
      const snapshot: WorkingHoursCacheSnapshot = await optimisticBreakPatch(
        queryClient,
        { weeklyWorkDayId: weeklyWorkDay.id, userId: weeklyWorkDay.employeeId },
        { start: weeklyWorkDay.startTime, end: weeklyWorkDay.endTime }
      );
      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshot) {
        restoreWorkingHoursCaches(queryClient, context.snapshot);
      }
    },
    onSuccess: async (data, variables) => {
      const userId = variables.employeeId;
      const workPlanId = variables.workPlan?.id;

      if (userId && workPlanId) {
        await queryClient.invalidateQueries({
          queryKey: userKeys.weeklyWorkDays.byWorkPlan(userId, workPlanId),
        });
      }
      if (userId) {
        await queryClient.invalidateQueries({
          queryKey: userKeys.weeklyWorkDays.byUser(userId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: calendarKeys.workingHours.all(),
      });
      queryClient.invalidateQueries({
        queryKey: calendarKeys.continuousWorkPlansForUsers.all(),
      });
    },
    meta: {
      successMessage: "Arbeitszeit erfolgreich aktualisiert",
      errorMessage: "Fehler beim Aktualisieren der Arbeitszeit",
    },
  });
};

/**
 * Mutation to delete a weekly work day
 * Replaces UserStore.deleteWeeklyWorkDay
 */
export const useDeleteWeeklyWorkDay = () => {
  const { user: userApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ weeklyWorkDayId }: { 
      weeklyWorkDayId: number; 
      userId: string;
      workPlanId?: number;
    }) => userApi.deleteWeeklyWorkDay(weeklyWorkDayId),
    onSuccess: async (data, variables) => {
      const { userId, workPlanId } = variables;
      if (userId && workPlanId) {
        await queryClient.invalidateQueries({
          queryKey: userKeys.weeklyWorkDays.byWorkPlan(userId, workPlanId),
        });
      }
      if (userId) {
        await queryClient.invalidateQueries({
          queryKey: userKeys.weeklyWorkDays.byUser(userId),
        });
      }
      // Refresh calendar-derived working hours so a break deleted from the
      // continuous (Dauertermin) calendar disappears without a reload
      // (same invalidations as useUpdateWeeklyWorkDay).
      queryClient.invalidateQueries({
        queryKey: calendarKeys.workingHours.all(),
      });
      queryClient.invalidateQueries({
        queryKey: calendarKeys.continuousWorkPlansForUsers.all(),
      });
    },
    onError: (_error, variables) => {
      // Blocked delete (§ 4 ArbZG): the calendar may have removed the break
      // event optimistically — refetch so it reappears immediately.
      const { userId, workPlanId } = variables;
      if (userId && workPlanId) {
        queryClient.invalidateQueries({
          queryKey: userKeys.weeklyWorkDays.byWorkPlan(userId, workPlanId),
        });
      }
      if (userId) {
        queryClient.invalidateQueries({
          queryKey: userKeys.weeklyWorkDays.byUser(userId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: calendarKeys.workingHours.all(),
      });
      queryClient.invalidateQueries({
        queryKey: calendarKeys.continuousWorkPlansForUsers.all(),
      });
    },
    meta: {
      successMessage: "Arbeitszeit gelöscht",
      errorMessage: "Fehler beim Löschen der Arbeitszeit",
    },
  });
};

/**
 * Mutation to save a location's calendar column order.
 *
 * `userIds` is the desired left-to-right order; the backend derives the ranks
 * from the array positions. On success every cached list that carries
 * `columnSortRank` is invalidated so the calendars re-sort their columns
 * without a reload.
 */
export const useSaveCalendarColumnOrder = () => {
  const { user: userApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ locationId, userIds }: { locationId: string; userIds: string[] }) =>
      userApi.saveCalendarColumnOrder({ locationId, userIds }),
    onSuccess: async () => {
      // The rank travels on the user payload, so the user lists themselves are
      // now stale in every calendar view.
      await queryClient.invalidateQueries({ queryKey: calendarKeys.usersByLocation.all() });
      await queryClient.invalidateQueries({ queryKey: calendarKeys.usersByRoleAndDate.all() });
      await queryClient.invalidateQueries({ queryKey: userKeys.allUsersBasic.all() });
    },
    meta: {
      successMessage: "Spaltenreihenfolge wurde gespeichert",
      errorMessage: "Fehler beim Speichern der Spaltenreihenfolge",
    },
  });
};
